import "../apex25.css";

import { Fragment, type ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { apex25LegalUrl, isApex25LegalPath } from "@/lib/apex25AppUrl";

import { Apex25Footer, Apex25Header } from "../Apex25Chrome";
import { APEX25_LEGAL_PAGES } from "../industries";
import { headingsOf, type InlineToken, type LegalBlock, tokenizeInline } from "./parse";
import {
  APEX25_LEGAL_AUDIENCES,
  APEX25_LEGAL_DOCS,
  APEX25_LEGAL_HUB_PATH,
  type Apex25LegalAudience,
  isLegalDocPath,
  legalDescription,
} from "./registry";

const renderToken = (token: InlineToken, key: number): ReactNode => {
  switch (token.type) {
    case "bold":
      return <strong key={key}>{token.text}</strong>;
    case "italic":
      return <em key={key}>{token.text}</em>;
    case "link":
      return (
        <a key={key} href={token.href}>
          {token.text}
        </a>
      );
    default:
      return <Fragment key={key}>{token.text}</Fragment>;
  }
};

export const Inline = ({ text }: { text: string }) => <>{tokenizeInline(text).map(renderToken)}</>;

const Block = ({ block }: { block: LegalBlock }) => {
  switch (block.type) {
    case "heading":
      return (
        <h2 id={block.id}>
          <Inline text={block.text} />
        </h2>
      );
    case "list":
      return (
        <ul>
          {block.items.map((item) => (
            <li key={item}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="a25-legal-table" role="region" tabIndex={0} aria-label="Table">
          <table>
            <thead>
              <tr>
                {block.header.map((cell, index) => (
                  <th key={index} scope="col">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, index) => (
                    <td key={index}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return (
        <p>
          <Inline text={block.text} />
        </p>
      );
  }
};

const AUDIENCE_HEADINGS: Record<Apex25LegalAudience, string> = {
  customer: "If your business uses Apex 25",
  candidate: "If you have been invited to an AI interview",
  everyone: "For everyone",
  demo: "If you are trying the demo",
};

const docPaths = Object.keys(APEX25_LEGAL_DOCS);

/** All the documents, so a reader of any one of them can reach the rest. */
const RelatedDocuments = ({ current }: { current?: string }) => (
  <nav className="a25-legal-related" aria-label="Apex 25 legal documents">
    <h2>Legal documents</h2>
    <ul>
      {docPaths.map((path) => (
        <li key={path}>
          {path === current ? (
            <span aria-current="page">{APEX25_LEGAL_DOCS[path].doc.title}</span>
          ) : (
            <a href={apex25LegalUrl(path)}>{APEX25_LEGAL_DOCS[path].doc.title}</a>
          )}
        </li>
      ))}
    </ul>
  </nav>
);

const Hub = () => (
  <>
    <p className="eyebrow">Legal</p>
    <h1>Apex 25 legal documents</h1>
    <p className="a25-legal-intro">
      Apex 25 is operated by Talentforge Innovations Pty Ltd (ABN 21 680 754 241). These are the
      documents that apply to Apex 25, our AI-assisted applicant tracking system, and to theRecruit.
    </p>
    {APEX25_LEGAL_AUDIENCES.map((audience) => (
      <section key={audience} aria-label={AUDIENCE_HEADINGS[audience]}>
        <h2>{AUDIENCE_HEADINGS[audience]}</h2>
        <ul className="a25-legal-cards">
          {docPaths
            .filter((path) => APEX25_LEGAL_DOCS[path].audience === audience)
            .map((path) => {
              const { doc } = APEX25_LEGAL_DOCS[path];
              return (
                <li key={path}>
                  <a href={apex25LegalUrl(path)}>{doc.title}</a>
                  <p>{legalDescription(path)}</p>
                  {doc.effective && <p className="a25-legal-effective">Effective {doc.effective}</p>}
                </li>
              );
            })}
        </ul>
      </section>
    ))}
    <p>
      Questions? Email <a href="mailto:support@theresume.io">support@theresume.io</a>.
    </p>
  </>
);

const Document = ({ path }: { path: string }) => {
  const { doc } = APEX25_LEGAL_DOCS[path];
  const sections = headingsOf(doc);
  // Everything before the first heading (the operator line, a status note) reads before the
  // contents list; the numbered sections follow it.
  const firstHeading = doc.blocks.findIndex((block) => block.type === "heading");
  const intro = firstHeading === -1 ? [] : doc.blocks.slice(0, firstHeading);
  const body = firstHeading === -1 ? doc.blocks : doc.blocks.slice(firstHeading);

  return (
    <>
      <p className="eyebrow">
        <a href={apex25LegalUrl(APEX25_LEGAL_HUB_PATH)}>Legal</a>
      </p>
      <h1>{doc.title}</h1>
      {doc.effective && <p className="a25-legal-effective">Effective date: {doc.effective}</p>}
      {intro.map((block, index) => (
        <Block key={`intro-${index}`} block={block} />
      ))}
      {sections.length > 2 && (
        <nav className="a25-legal-toc" aria-label="Contents">
          <h2>Contents</h2>
          <ol>
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.text.replace(/^\d+\.\s+/, "")}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      {body.map((block, index) => (
        <Block key={index} block={block} />
      ))}
      <RelatedDocuments current={path} />
    </>
  );
};

/**
 * One page of the Apex 25 legal set: the hub at /legal, or one of the documents. Clerk-free like
 * the rest of the Apex 25 marketing pages, so it also works on tryapex25.com.
 */
export const Apex25LegalPage = ({ path }: { path: string }) => {
  useEffect(() => {
    document.title = APEX25_LEGAL_PAGES[path]?.title ?? "Apex 25 legal documents";
    // The page renders after a lazy load, so the browser's own jump to "#section-4" has nothing to
    // land on yet. Do it once the content is here, and again when the web fonts arrive: they move
    // the text, and a heading would otherwise end up under the sticky header.
    const anchor = window.location.hash.slice(1);
    if (!anchor) return;
    const scroll = () => document.getElementById(anchor)?.scrollIntoView();
    scroll();
    void document.fonts?.ready.then(scroll);
  }, [path]);

  return (
    <div className="apex25">
      <Apex25Header onHome={false} />
      <main>
        <section className="a25-terms-page a25-legal">
          <div className="wrap">
            {isLegalDocPath(path) ? <Document path={path} /> : <Hub />}
          </div>
        </section>
      </main>
      <Apex25Footer onHome={false} />
    </div>
  );
};

/**
 * The route element: works out which page from the URL, so the router can lazy-load it without
 * props. The in-app copies (staging, localhost) sit under /apex25.
 */
export const Apex25LegalRoute = () => {
  const { pathname } = useLocation();
  const path = pathname.replace(/^\/apex25(?=\/)/, "").replace(/\/+$/, "");
  return <Apex25LegalPage path={isApex25LegalPath(path) ? path : APEX25_LEGAL_HUB_PATH} />;
};

export default Apex25LegalPage;
